/**
 * HypertrophyDashboard — bespoke body for the "Hypertrophy" persona.
 *
 * The heavy lifter / bodybuilder. Their north star is weekly working
 * VOLUME per muscle against the productive range (MEV→MAV→MRV), backed
 * by whether the lifts are actually progressing, whether protein is
 * fueling the work, and where the physique is trending. They don't care
 * about tactical readiness theatrics or a game layer — they care about
 * stimulus, recovery, and the scale/mirror over time.
 *
 * Layout:
 *   1. Start Workout (the lift)
 *   2. Volume by Muscle — THE card. MEV/MAV/MRV bands per muscle.
 *   3. Exercise Progress — which lifts are moving up / stalling / down.
 *   4. Fuel — protein / calories / steps (protein is the build input)
 *   5. Nutrition × Training — does hitting protein actually raise next-
 *      session quality? Proof the fuel matters, not just a number.
 *   6. Body Composition Velocity — fat vs lean trajectory
 *
 * Reuses the existing analytics cards + their pure computes.
 */

'use client';

import { useEffect, useState } from 'react';
import { Dumbbell, TrendingUp, Beef } from 'lucide-react';
import StartWorkoutCard from '@/components/dashboard/StartWorkoutCard';
import DailyStatusRow from '@/components/dashboard/DailyStatusRow';
import WeightStrip from '@/components/dashboard/WeightStrip';
import VolumeByMuscleCard from '@/components/metrics-ops/VolumeByMuscleCard';
import BodyCompVelocityCard from '@/components/metrics-ops/BodyCompVelocityCard';
import ExerciseProgressCard from '@/components/metrics-ops/ExerciseProgressCard';
import NutritionSynergyCard from '@/components/metrics-ops/NutritionSynergyCard';
import type { VolumeByMuscleSummary } from '@/lib/volumeByMuscle';
import type { BodyCompVelocitySummary } from '@/lib/bodyCompositionVelocity';
import type { NutritionTrainingSynergy } from '@/lib/nutritionTrainingSynergy';
import type { DashboardData } from '@/lib/hooks/useDashboardData';

type Tone = 'good' | 'warn' | 'bad' | 'idle';

const TONE: Record<Tone, { tw: string; bar: string; border: string }> = {
  good: { tw: 'text-emerald-400', bar: 'bg-emerald-400', border: 'border-emerald-500/40' },
  warn: { tw: 'text-amber-400',   bar: 'bg-amber-400',   border: 'border-amber-500/40' },
  bad:  { tw: 'text-red-400',     bar: 'bg-red-400',     border: 'border-red-500/40' },
  idle: { tw: 'text-slate-500',   bar: 'bg-slate-600',   border: 'border-slate-700' },
};

interface GrowthCell { label: string; value: string; sub: string; tone: Tone; Icon: typeof Dumbbell }

export default function HypertrophyDashboard({ data }: { data: DashboardData }) {
  const { user, todayActivity, dailyCalories, activeBreak } = data;
  const intelligence = data.intelligenceReport;
  const [volume, setVolume] = useState<VolumeByMuscleSummary | null>(null);
  const [bodyComp, setBodyComp] = useState<BodyCompVelocitySummary | null>(null);
  const [synergy, setSynergy] = useState<NutritionTrainingSynergy | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [synergyLoaded, setSynergyLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.uid) { setLoaded(true); setSynergyLoaded(true); return; }
      try {
        const [{ getSessionHistory }, { getDailyActivityEntries }, vol, bc] = await Promise.all([
          import('@/lib/sessionStorage'),
          import('@/lib/db'),
          import('@/lib/volumeByMuscle'),
          import('@/lib/bodyCompositionVelocity'),
        ]);
        const since = new Date(Date.now() - 90 * 86_400_000);
        const [sessions, activities] = await Promise.all([
          getSessionHistory({ limit: 200 }),
          getDailyActivityEntries(user.uid, { since }),
        ]);
        if (cancelled) return;
        setVolume(vol.computeVolumeByMuscle(sessions, 7));
        setBodyComp(bc.computeBodyCompVelocity(activities as any, 90));
        setLoaded(true);

        // Nutrition × training synergy — walk the last 60 days of food
        // logs and join against next-session quality. Heavier, so it
        // runs after the core cards are on screen.
        try {
          const [{ getDailySummary, getNutritionGoals }, { computeNutritionTrainingSynergy }] = await Promise.all([
            import('@/lib/nutritionService'),
            import('@/lib/nutritionTrainingSynergy'),
          ]);
          const goals = await getNutritionGoals(user.uid).catch(() => null);
          const nutritionByDay = new Map<string, any>();
          const today = new Date(); today.setHours(0, 0, 0, 0);
          for (let i = 0; i < 60; i++) {
            if (cancelled) return;
            const d = new Date(today); d.setDate(d.getDate() - i);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            try {
              const summary = await getDailySummary(user.uid, key);
              if (summary && summary.totalCalories > 0) nutritionByDay.set(key, summary);
            } catch { /* per-day fetch is best-effort */ }
          }
          if (cancelled) return;
          setSynergy(computeNutritionTrainingSynergy({
            sessions,
            nutritionByDay,
            nutritionGoals: goals,
            recentActivities: activities as any,
          }));
        } catch (e) {
          console.warn('[hypertrophy-dash] synergy failed:', e);
        } finally {
          if (!cancelled) setSynergyLoaded(true);
        }
      } catch (e) {
        console.warn('[hypertrophy-dash] load failed:', e);
        if (!cancelled) { setLoaded(true); setSynergyLoaded(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // ── GROWTH STATUS — synthesize the three hypertrophy levers (volume vs
  //    productive range · lift progression · protein) into one verdict.
  const vs = intelligence?.extended?.volumeStatus ?? null;
  const ns = intelligence?.extended?.nutritionStatus ?? null;
  const ready = intelligence?.progressions?.weights?.ready?.length ?? 0;
  const stalls = intelligence?.predictions?.plateauWarnings?.length ?? 0;
  const proteinPerLb = ns?.proteinPerLbBodyweight ?? null;
  const proteinLow = ns?.proteinAdequacy === 'low';
  /**
   * The REAL domain is VolumeStatus from lib/minimumEffectiveDose.ts:28 —
   * 'below-med' | 'at-med' | 'optimal' | 'approaching-mrv' | 'above-mrv'.
   *
   * This used to be commented "'under' | 'optimal' | 'over'" and tested
   * against those three strings. Only 'optimal' is in both sets, so the
   * OVERREACHING and UNDER-STIMULATED branches were unreachable: a lifter
   * ABOVE MRV fell through to "MAINTAINING — push volume or load to drive new
   * growth", the exact opposite of the engine's finding. The field is typed as
   * a bare `string` on ExtendedIntelligenceData, so tsc could not catch it.
   */
  const volState = (vs?.overallVolumeStatus ?? null) as
    | 'below-med' | 'at-med' | 'optimal' | 'approaching-mrv' | 'above-mrv' | null;
  const volOver = volState === 'above-mrv' || volState === 'approaching-mrv';
  const volUnder = volState === 'below-med' || volState === 'at-med';

  /** Human label for the raw enum — the tile used to print "above-mrv". */
  const VOL_LABEL: Record<string, string> = {
    'below-med': 'below minimum',
    'at-med': 'at minimum',
    'optimal': 'optimal',
    'approaching-mrv': 'near ceiling',
    'above-mrv': 'over ceiling',
  };

  const verdict: { word: string; tone: Tone; sub: string } = (() => {
    if (!vs && proteinPerLb == null && ready === 0 && stalls === 0) {
      return { word: 'CALIBRATING', tone: 'idle', sub: 'Log a few sessions to read your growth signal' };
    }
    if (volOver) {
      return { word: 'OVERREACHING', tone: 'bad', sub: 'Weekly volume above your recoverable ceiling — back off' };
    }
    if (volUnder || proteinLow) {
      return { word: 'UNDER-STIMULATED', tone: 'warn', sub: volUnder ? 'Volume below the productive range — add sets' : 'Protein below the build threshold — raise it' };
    }
    if (volState === 'optimal' && ready > 0) {
      return { word: 'BUILDING', tone: 'good', sub: `Volume on target · ${ready} lift${ready === 1 ? '' : 's'} ready to progress` };
    }
    return { word: 'MAINTAINING', tone: 'warn', sub: 'Holding steady — push volume or load to drive new growth' };
  })();
  const vt = TONE[verdict.tone];

  const growthCells: GrowthCell[] = [
    {
      label: 'Weekly Sets',
      value: vs ? `${vs.totalWeeklySets}${vs.recommendedTotalSets > 0 ? `/${vs.recommendedTotalSets}` : ''}` : '—',
      sub: volState ? (VOL_LABEL[volState] ?? volState) : 'no data',
      tone: volState === 'optimal' ? 'good' : volOver ? 'bad' : volUnder ? 'warn' : 'idle',
      Icon: Dumbbell,
    },
    {
      label: 'Progressing',
      value: `${ready}↑`,
      sub: stalls > 0 ? `${stalls} stalling` : ready > 0 ? 'moving up' : 'no movement yet',
      tone: stalls > 0 && ready === 0 ? 'warn' : ready > 0 ? 'good' : 'idle',
      Icon: TrendingUp,
    },
    {
      label: 'Protein g/lb',
      value: proteinPerLb != null ? proteinPerLb.toFixed(2) : '—',
      sub: ns?.proteinAdequacy ?? 'no data',
      tone: proteinPerLb == null ? 'idle' : proteinLow ? 'warn' : 'good',
      Icon: Beef,
    },
  ];

  return (
    <div className="space-y-3">
      {/* GROWTH STATUS — the glanceable synthesis. The cards below prove it. */}
      <section className={`relative overflow-hidden rounded-xl border ${vt.border} bg-hud-ink/85`}>
        <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-30" />
        <header className="px-4 py-1.5 border-b border-slate-800/60 font-mono text-[9px] uppercase tracking-[0.28em] text-slate-500 flex items-center gap-2">
          <Dumbbell className="w-3 h-3 text-operator-gold/70" />
          <span>Growth Status · Stimulus · SYS·HYP·01</span>
        </header>
        <div className="px-4 py-3 flex items-center gap-4 border-b border-slate-800/50">
          <span className={`w-3 h-3 rounded-full ${vt.bar} animate-hud-blink shrink-0`} aria-hidden />
          <div className="min-w-0 flex-1">
            <div className={`font-heading text-2xl font-bold tracking-wide ${vt.tw}`}>{verdict.word}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400 mt-0.5 normal-case">{verdict.sub}</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-px bg-slate-800/50">
          {growthCells.map((c) => {
            const t = TONE[c.tone];
            const Icon = c.Icon;
            return (
              <div key={c.label} className="bg-hud-ink/80 px-3 py-2.5 flex flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <Icon className={`w-3 h-3 ${t.tw}`} aria-hidden />
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">{c.label}</span>
                </div>
                <div className={`font-heading text-lg font-bold tabular-nums leading-none ${t.tw}`}>{c.value}</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500 truncate">{c.sub}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 1. Start the lift */}
      {!activeBreak && <StartWorkoutCard data={data} />}

      {/* 2. Volume by muscle — the hypertrophy north star */}
      {loaded && <VolumeByMuscleCard summary={volume} />}

      {/* 3. Are the lifts actually moving? Progress / stall / regress. */}
      {intelligence && <ExerciseProgressCard intelligence={intelligence} limit={4} />}

      {/* 4. Fuel — protein is the build input */}
      <DailyStatusRow userId={user?.uid} todayActivity={todayActivity} dailyCalories={dailyCalories} />

      {/* 4b. Weight trend — lean-bulkers still need the scale read. */}
      <WeightStrip userId={user?.uid} />

      {/* 5. Does the fuel actually move output? Protein → next session. */}
      {synergyLoaded && <NutritionSynergyCard summary={synergy} />}

      {/* 6. Physique trajectory — fat vs lean */}
      {loaded && <BodyCompVelocityCard summary={bodyComp} />}
    </div>
  );
}
